import {
    Dispatch,
    ReducersMapObject,
    ThunkDispatch,
    UnknownAction,
} from "@reduxjs/toolkit";
import { castDraft, produce } from "immer";
import { useMemo } from "react";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { createTransform } from "redux-persist";

// Defaults and constants
import { Step } from "../apps/checkout/constants";
import { defaults as checkoutDefaults } from "../apps/checkout/defaults";
// Common Reducers
import { reducers as checkoutReducers } from "../apps/checkout/reducers";
import { reducers as configuratorReducers } from "../apps/configurator/reducers";
import { reducers as csrReducers } from "../apps/csr/reducers";
import { financingDefaults, financingSlice } from "../apps/financing";
import { mattressMatchSlice } from "../apps/mattress-match/reducers";
import { reducers as compareReducers } from "../apps/product-compare/reducers";
import productGrid2Reducer from "../apps/product-grid2/reducers";
import { retailSlice } from "../apps/retail/reducers";
import { reducers as reviewsReducers } from "../apps/reviews/reducers";
import {
    reducers as commonReducers,
    getInitialViewport,
} from "./common/reducers";
import { IRootState } from "./reducers.interfaces";

// ============================================================================
// Store Reset Action
// ============================================================================

/**
 * Action type for resetting the entire Redux store to default state.
 * Used during SSR to prevent data leaking between requests.
 */
export const RESET_STORE = "@@RESET_STORE";

/**
 * Action creator for resetting the entire Redux store to default state.
 * Should be dispatched at the start of each SSR request.
 */
export const resetStore = () => ({ type: RESET_STORE });

// Combine reducers into map
export const coreReducers: ReducersMapObject<IRootState> = {
    checkout: checkoutReducers,
    common: commonReducers,
    configurator: configuratorReducers,
    csr: csrReducers,
    mattressmatch: mattressMatchSlice.reducer,
    financing: financingSlice.reducer,
    productcompare: compareReducers,
    productgrid2: productGrid2Reducer,
    retail: retailSlice.reducer,
    reviews: reviewsReducers,
};

export type AppDispatch = ThunkDispatch<IRootState, undefined, UnknownAction> &
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    Dispatch<any>;

// Hooks
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<IRootState> = useSelector;

/**
 * Prefill Redux store with SSR data synchronously during render.
 *
 * Uses useMemo (not useEffect) because this must run during SSR
 * before child components render. The store is a shared singleton
 * across SSR and client components, so per-request stores aren't viable.
 */
export const useSSRStorePrefill = <T extends object>(
    data: T,
    fn: (dispatch: AppDispatch, data: T) => void,
): void => {
    const dispatch = useAppDispatch();
    useMemo(() => {
        fn(dispatch, data);
    }, Object.values(data));
};

// Persist config
export const sanitizeCommonData = createTransform(
    (state: IRootState["common"]) => {
        return {
            ...state,
            user: null,
        };
    },
    (state: IRootState["common"]) => {
        // A persisted viewport belongs to a previous document/window size.
        return { ...state, viewport: getInitialViewport() };
    },
    {
        whitelist: ["common"],
    },
);

export const sanitizeCheckoutData = createTransform(
    (state: IRootState["checkout"]) => {
        return produce(state, (draft) => {
            draft.data.assisted_user = null;
            draft.data.order = null;
            draft.data.deferredPaymentOrder = null;
            // A restored copy would falsely report an in-flight payment.
            draft.data.payment_states = null;
            draft.form.payment_methods = checkoutDefaults.form.payment_methods;
            draft.gifts = castDraft(checkoutDefaults.gifts);
        });
    },
    (state: IRootState["checkout"]) => {
        return produce(state, (draft) => {
            // Backfill fields that may be missing from old persisted state
            if (!draft.data.predicted_delivery_date) {
                draft.data.predicted_delivery_date = castDraft(
                    checkoutDefaults.data.predicted_delivery_date,
                );
            }
            // Discard payment states persisted by older code
            draft.data.payment_states = null;
            if (state.form.current_step >= Step.PLACING_ORDER) {
                draft.form.current_step = Step.PAYMENT_METHODS;
            }
        });
    },
    {
        whitelist: ["checkout"],
    },
);

export const resetFinancingData = createTransform(
    (state: IRootState["financing"]) => {
        return produce(state, (s) => {
            s.modal = castDraft(financingDefaults.modal);
            s.preQualApproval = castDraft(financingDefaults.preQualApproval);
        });
    },
    (state: IRootState["financing"]) => {
        return state;
    },
    {
        whitelist: ["financing"],
    },
);
